import { db } from "./api";
import { useQuery } from "./hooks";

export type ActivityType = {
  name: string; counts_as: string | null; give_ask: "give" | "ask" | "neutral";
  two_way: boolean; is_touch: boolean; stage_level: number; is_skip: boolean; is_note: boolean; sort: number;
};
export type Area = "warm" | "networking" | "hnw" | "travel" | "new_connections" | "team" | "clients" | "other";

export const AREA_LABEL: Record<Area, string> = {
  warm: "Warm market", networking: "People met", hnw: "HNW", travel: "Travel",
  new_connections: "New connections", team: "Team", clients: "Clients", other: "Other",
};
export const LEAD_AREAS: Area[] = ["warm", "networking", "hnw", "travel", "new_connections"];

export const COUNT_LABEL: Record<string, string> = {
  outreach: "New outreach", follow_ups: "Follow-ups", appointments: "Appointments",
  client_moves: "Client moves", recruit_moves: "Recruit moves", builder_moves: "Builder moves",
};

/** All the reference lists, loaded once and cached */
export function useRefData() {
  return useQuery("ref:all", async () => {
    const [lists, types, typeAreas, settings, stages, relTypes, milestones] = await Promise.all([
      db.select<{ list_key: string; value: string; sort: number; is_active: boolean }>("list_items", { select: "list_key,value,sort,is_active", order: "list_key,sort" }),
      db.select<ActivityType>("activity_types", { select: "*", is_active: "eq.true", order: "sort" }),
      db.select<{ activity_type: string; area: Area; sort: number }>("activity_type_areas", { select: "*", order: "sort" }),
      db.select<{ key: string; value: any }>("settings", { select: "key,value" }),
      db.select<{ board: string; stage: string; sort: number; is_won_stage: boolean }>("pipeline_stages", { select: "*", order: "board,sort" }),
      db.select<{ key: string; label: string; inverse_key: string | null; sort: number }>("relationship_types", { select: "*", order: "sort" }),
      db.select<{ key: string; label: string; due_label: string | null; due_days: number | null; is_count: boolean; sort: number }>("milestone_defs", { select: "*", is_active: "eq.true", order: "sort" }),
    ]);
    const list = (k: string) => lists.filter((l) => l.list_key === k && l.is_active).map((l) => l.value);
    const typesFor = (area: Area) => {
      const names = typeAreas.filter((t) => t.area === area).map((t) => t.activity_type);
      return names.map((n) => types.find((t) => t.name === n)).filter(Boolean) as ActivityType[];
    };
    const setting = (k: string) => settings.find((s) => s.key === k)?.value;
    const boards = [...new Set(stages.map((s) => s.board))].sort((a, b) => (a === "Solution" ? -1 : b === "Solution" ? 1 : a.localeCompare(b)));
    const stagesFor = (b: string) => stages.filter((s) => s.board === b).map((s) => s.stage);
    const isWon = (b: string, st: string) => stages.some((s) => s.board === b && s.stage === st && s.is_won_stage);
    return { list, types, typesFor, setting, boards, stagesFor, isWon, relTypes, milestones, raw: { lists, settings } };
  }, { staleMs: 5 * 60_000 });
}
